import React, { useState, useEffect, useRef } from "react";
import { apiGet, apiPost } from "../../services/api";
import { emitFinanceEvent } from "../manager/finance/financeDispatcher";
import PatientSearchPanel from "./PatientSearchPanel";
import PrescriptionLoader from "./PrescriptionLoader";
import LowStockAlertPanel from "./LowStockAlertPanel";
import TodayCollectionWidget from "./TodayCollectionWidget";
import MonthlyCollectionWidget from "./MonthlyCollectionWidget";
import { useToast } from "../common/ToastProvider";
import Button from "../common/Button";
import "./Pharmacy.css";

interface PharmacyPatient {
  patient_id: string;
  name?: string;
  [k: string]: unknown;
}

interface DrugSearchItem {
  id: number | string;
  name?: string;
  stock?: number;
  price?: number;
  selling_price?: number;
  stock_qty?: number;
  [k: string]: unknown;
}

interface DrugGroup {
  group_id: number;
  group_name: string;
  drugs?: DrugSearchItem[];
}

interface CartItem {
  id: number | string;
  name?: string;
  type: string;
  quantity: number;
  price: number;
  [k: string]: unknown;
}

interface PrescriptionMedicine {
  drug_id?: number | string | null;
  drug_name?: string;
  days?: number;
  [k: string]: unknown;
}

export default function PharmacyBillingPanel({ onOpenSalesHistory }: { onOpenSalesHistory?: () => void } = {}) {
  const [patient, setPatient] = useState<PharmacyPatient | null>(null);
  const [billType, setBillType] = useState<"Sale" | "Self">("Sale");

  const [drugQuery, setDrugQuery] = useState("");
  const [drugResults, setDrugResults] = useState<DrugSearchItem[]>([]);
  const [drugGroups, setDrugGroups] = useState<DrugGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<DrugGroup | null>(null);

  const [cart, setCart] = useState<CartItem[]>([]);
  const [materialCost, setMaterialCost] = useState("");
  const [saving, setSaving] = useState(false);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const { showToast } = useToast();

  useEffect(() => {
    const loadGroups = async () => {
      try {
        const res = (await apiGet("/api/pharmacy/drug-groups")) as DrugGroup[] | { error?: string };
        if (Array.isArray(res)) {
          setDrugGroups(res);
        } else if (res && (res as { error?: string }).error) {
          setDrugGroups([]);
        } else {
          setDrugGroups([]);
        }
      } catch {
        setDrugGroups([]);
      }
    };
    loadGroups();
  }, []);

  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (drugQuery.length < 2) {
      setDrugResults([]);
      return;
    }
    const t = setTimeout(() => {
      apiGet(`/api/pharmacy/drugs/search?q=${encodeURIComponent(drugQuery)}`)
        .then((res: unknown) => {
          setDrugResults(Array.isArray(res) ? (res as DrugSearchItem[]) : []);
        })
        .catch((err) => {
          console.error("Drug search error:", err);
          setDrugResults([]);
        });
    }, 300);
    return () => clearTimeout(t);
  }, [drugQuery]);

  const addToCart = (item: DrugSearchItem | CartItem, type: "DRUG" | "SERVICE" | "MATERIAL" = "DRUG") => {
    setCart((prev) => {
      const existing = prev.find((i) => i.id === item.id && i.type === type);
      if (existing) {
        return prev.map((i) =>
          i.id === item.id && i.type === type ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      const resolvedPrice = Number(
        (item as { selling_price?: number }).selling_price ?? (item as { price?: number }).price ?? 0
      );
      const cartItem: CartItem = {
        ...item,
        name: (item as { name?: string }).name ?? "Unknown",
        type,
        quantity: 1,
        price: resolvedPrice,
      };
      return [...prev, cartItem];
    });
    setDrugQuery("");
    setDrugResults([]);
  };

  const handleGroupSelect = (group: DrugGroup | null) => {
    if (!group?.drugs || !Array.isArray(group.drugs)) {
      showToast({
        message: `No drugs found in group "${group?.group_name ?? "Unknown"}"`,
        type: "info",
      });
      return;
    }
    let addedCount = 0;
    let skippedCount = 0;
    group.drugs.forEach((drug) => {
      const qty = drug.stock_qty ?? (drug as { stock?: number }).stock ?? 0;
      if (qty > 0) {
        addToCart(
          { ...drug, price: drug.selling_price ?? (drug as { price?: number }).price ?? 0 },
          "DRUG"
        );
        addedCount++;
      } else {
        skippedCount++;
      }
    });
    if (addedCount > 0) {
      showToast({
        message: `Added ${addedCount} drug(s) from "${group.group_name}"${
          skippedCount > 0 ? ` (${skippedCount} skipped – no stock)` : ""
        }`,
        type: "success",
      });
    } else if (skippedCount > 0) {
      showToast({
        message: `No drugs with stock available in "${group.group_name}" (${skippedCount} drugs have 0 stock)`,
        type: "info",
      });
    } else {
      showToast({
        message: `No drugs found in "${group.group_name}"`,
        type: "info",
      });
    }
  };

  const handleLoadPrescription = (medicines: PrescriptionMedicine[]) => {
    medicines.forEach((med) => {
      if (med.drug_id) {
        apiGet(`/api/pharmacy/drugs/search?q=${encodeURIComponent(med.drug_name ?? "")}`)
          .then((res: unknown) => {
            const arr = Array.isArray(res) ? (res as DrugSearchItem[]) : [];
            if (arr.length > 0) {
              const drug = arr[0];
              addToCart({ ...drug, quantity: med.days ?? 1 } as DrugSearchItem & { quantity: number }, "DRUG");
            }
          })
          .catch(() => {});
      }
    });
  };

  const addService = (name: string, price: number) => {
    const id = name.replace(/\s/g, "_");
    addToCart({ id, name, price, quantity: 1 } as CartItem, "SERVICE");
  };

  const addMaterial = () => {
    const n = parseFloat(materialCost);
    if (!materialCost || isNaN(n)) return;
    const id = `MAT_${Date.now()}`;
    addToCart(
      { id, name: "Material / Misc", price: n, quantity: 1 } as CartItem,
      "MATERIAL"
    );
    setMaterialCost("");
  };

  const removeFromCart = (idx: number) => {
    setCart((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    if (cart.length === 0) return;
    setSaving(true);
    try {
      const payload = {
        patient_id: billType === "Sale" && patient ? patient.patient_id : null,
        patient_name: billType === "Sale" && patient ? patient.name : "Walk-in",
        bill_type: billType,
        total: grandTotal,
        items: cart.map(i => ({
          drug_id: i.id,
          name: i.name,
          type: i.type,
          quantity: i.quantity,
          unit_price: i.price,
          subtotal: i.price * i.quantity,
          price: i.price // Ensuring backend compat mapping
        }))
      };

      const res = await apiPost("/api/pharmacy/bill", payload);
      const newBillId = (res as any).bill_id;
      showToast({ message: billType === "Self" ? "Stock deducted successfully." : "Bill generated successfully.", type: "success" });
      
      setCart([]);
      setPatient(null);
      if (billType === "Sale" && newBillId) {
        window.open(`/print?type=pharmacy&bill=${newBillId}`, "_blank");
        
        // Emit Finance Event for Pharmacy Sale
        if (grandTotal > 0) {
          await emitFinanceEvent({
            source: "PHARMACY_SALE",
            ref_id: String(newBillId),
            debit: grandTotal,
            credit: 0,
            description: `Pharmacy sale (Bill #${newBillId}) - ${patient?.name || "Walk-in"}`,
            meta: {
              bill_id: newBillId,
              patient_id: patient?.patient_id || null,
              bill_type: "Sale"
            }
          });
        }
      }
      window.dispatchEvent(new Event("pharmacy:billSaved"));
    } catch (e) {
      showToast({ message: "Failed to process checkout.", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const grandTotal =
    billType === "Self" ? 0 : cart.reduce((acc, i) => acc + i.price * i.quantity, 0);

  return (
    <div className="pharmacy-billing-layout">
      <div className="pharmacy-billing-main">
        <div className="pharmacy-panel">
          <div className="billing-header">
            <div className="type-toggle">
              <label className={billType === "Sale" ? "active" : ""}>
                <input
                  type="radio"
                  checked={billType === "Sale"}
                  onChange={() => setBillType("Sale")}
                />
                Patient Sale
              </label>
              <label className={billType === "Self" ? "active-self" : ""}>
                <input
                  type="radio"
                  checked={billType === "Self"}
                  onChange={() => setBillType("Self")}
                />
                Self / Clinic Use
              </label>
            </div>

            {billType === "Sale" && (
              <div className="patient-box">
                {patient ? (
                  <div className="selected-patient">
                    <span>👤 {patient.name ?? "Unknown Patient"}</span>
                    <button type="button" onClick={() => setPatient(null)}>
                      ✕
                    </button>
                  </div>
                ) : (
                  <PatientSearchPanel onSelect={(p) => setPatient(p as PharmacyPatient)} />
                )}
                {patient && (
                  <PrescriptionLoader
                    patient={patient}
                    onLoadPrescription={handleLoadPrescription}
                  />
                )}
              </div>
            )}
          </div>

          <div className="billing-actions">
            <div className="drug-groups-box">
              <label>📦 Quick Load Drug Groups:</label>
              <select
                className="drug-group-select"
                value={selectedGroup?.group_id ?? ""}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  const value = e.target.value;
                  if (!value) {
                    setSelectedGroup(null);
                    return;
                  }
                  const group = drugGroups.find((g) => g.group_id === parseInt(value, 10));
                  if (group) {
                    handleGroupSelect(group);
                    e.target.value = "";
                    setSelectedGroup(null);
                  }
                }}
              >
                <option value="">
                  {drugGroups.length > 0
                    ? `Select Drug Group... (${drugGroups.length} available)`
                    : "No drug groups available. Create groups in Admin panel."}
                </option>
                {drugGroups.map((g) => (
                  <option key={g.group_id} value={g.group_id}>
                    {g.group_name} ({g.drugs?.length ?? 0} drugs)
                  </option>
                ))}
              </select>
              {drugGroups.length === 0 && (
                <small
                  style={{
                    color: "#64748b",
                    fontSize: "0.85rem",
                    marginTop: "4px",
                    display: "block",
                  }}
                >
                  💡 Tip: Create drug groups in Admin → Master Data → Drug Groups
                </small>
              )}
            </div>

            <div className="drug-search-box">
              <input
                ref={searchInputRef}
                placeholder="Type to search medicine..."
                value={drugQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDrugQuery(e.target.value)}
              />
              {drugResults.length > 0 && (
                <div className="search-dropdown">
                  {drugResults.map((d) => (
                    <div
                      key={d.id}
                      className="dropdown-item"
                      onClick={() => addToCart(d, "DRUG")}
                    >
                      <div className="d-name">{d.name ?? "Unknown Drug"}</div>
                      <div className="d-meta">
                        Stock: {(d as { stock?: number }).stock ?? 0} | ₹
                        {(d as { selling_price?: number }).selling_price ?? (d as { price?: number }).price ?? 0}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="services-row">
              <button
                type="button"
                className="btn-svc"
                onClick={() => addService("X-Ray", 150)}
              >
                🩻 X-Ray
              </button>
              <button
                type="button"
                className="btn-svc"
                onClick={() => addService("Consultation", 150)}
              >
                👨‍⚕️ Consult
              </button>
              <button
                type="button"
                className="btn-svc"
                onClick={() => addService("Sugar Test", 50)}
              >
                🩸 Sugar
              </button>
            </div>

            <div className="material-input">
              <input
                type="number"
                placeholder="Material Cost ₹"
                value={materialCost}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setMaterialCost(e.target.value)
                }
              />
              <button type="button" onClick={addMaterial}>
                Add
              </button>
            </div>
          </div>

          <div className="cart-table-wrapper">
            <table className="cart-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Type</th>
                  <th style={{ width: 80 }}>Qty</th>
                  <th style={{ width: 100 }}>Price</th>
                  <th style={{ width: 100 }}>Total</th>
                  <th style={{ width: 50 }}></th>
                </tr>
              </thead>
              <tbody>
                {cart.map((item, idx) => (
                  <tr key={idx}>
                    <td>{item.name ?? "Unknown Item"}</td>
                    <td>
                      <span className={`badge ${item.type}`}>{item.type}</span>
                    </td>
                    <td>
                      <input
                        type="number"
                        className="qty-input"
                        value={item.quantity}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const val = parseFloat(e.target.value);
                          setCart((c) =>
                            c.map((it, i) =>
                              i === idx ? { ...it, quantity: val } : it
                            )
                          );
                        }}
                      />
                    </td>
                    <td>
                      {billType === "Self" ? (
                        <s>₹{item.price}</s>
                      ) : (
                        `₹${item.price}`
                      )}
                    </td>
                    <td>
                      {billType === "Self"
                        ? "₹0"
                        : `₹${(item.price * item.quantity).toFixed(2)}`}
                    </td>
                    <td>
                      <Button variant="ghost" size="sm" onClick={() => removeFromCart(idx)} style={{ color: '#ef4444' }}>
                        ✕ Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="billing-footer">
            <div className="grand-total">
              Total: <span>₹{grandTotal.toLocaleString()}</span>
            </div>
            <Button 
                variant="primary" 
                size="lg" 
                onClick={handleSave} 
                disabled={cart.length === 0} 
                isLoading={saving}
            >
                {billType === "Self" ? "📉 Deduct Stock" : "🖨️ Print & Finalize Bill"}
            </Button>
          </div>
        </div>
      </div>
      <div className="pharmacy-billing-right">
        <MonthlyCollectionWidget />
        <TodayCollectionWidget onOpenHistory={onOpenSalesHistory} />
        <LowStockAlertPanel />
      </div>
    </div>
  );
}
